import { Box, Button, Container, Paper, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProvidersDetails } from '../../../providers/components/providers-details/providers-details.component';
import {
	useGenerateEngagementsCustomFieldFileUrlMutation,
	useGetAllEngagementStatusesQuery,
	useGetEngagementFromLinguistLinkQuery,
	useGetEngagementQuery,
	useUpdateEngagementMutation,
} from '../../../../store/api/engagements.api';
import { EngagementDetails } from '../../components/engagement-details/engagement-details.component';
import { useSortingParams } from '../../../_core/hooks';
import { FamilyDetailsList } from '../../../family/components/family-details/family-details-list.component';
import {
	useGetAllSurveyTemplatesListQuery,
	useCheckExistEngagementSurveyQuery,
	useGetListEngagementFamiliesWithSurveyQuery,
	useGetSurveySummaryQuery,
	useGetSurveyUnrecognizedAnswersQuery,
	useGetCommunicationLogQuery,
	useGetListEngagementFamiliesWithSurveyLinguistLinkQuery,
} from '../../../../store/api/survey.api';
import { useLocation, useParams } from 'react-router-dom';
import { useSessionStorage } from '../../../../hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { NotFoundComponent, ForbiddenComponent } from '../../../_core/components/exceptions';
import { useGetAllFieldsQuery } from '../../../../store/api/customRequirements.api';
import { useGetAllProvidersByDistrictIdQuery } from '../../../../store/api/providers.api';
import {
	useGetAllFamiliesByDistrictIdQuery,
	useGetChildrenByDistrictIdQuery,
} from '../../../../store/api/families.api';
import { useOpenModalForm } from '../../hooks/modal-forms/use-open-modal-form.hook';
import { AvatarIcon } from '../../../_core/constants/icons.constants';
import { CommunicationLogsModal } from '../../components/communication-log/communication-log.component';
import { HistoryRoundedIcon } from '../../../_core/components/icons';
import { useAppDispatch } from '../../../../store';
import { initiateEngagement } from '../../store/actions/initiate-engagement-state.action';
import { setLocalStorageDistrictId, setLocalStorageEngagementId, setLocalStorageEngagementItemId } from '../../../_core/utils';
import { useGetDistrictQuery } from '../../../../store/api/district.api';

export const EngagementDetailsPage: FC = () => {
	const [t] = useTranslation();
	const dispatch = useAppDispatch();
	const [sessionStorageDistrictId, setSessionStorageDistrictId] = useSessionStorage('districtId');

	const { engagementId, engagementItemId } = useParams<{ engagementId: string; engagementItemId?: string }>();

	const location = useLocation();
	const isAdminRoute = location.pathname.startsWith('/admin');
	const searchParams = new URLSearchParams(location.search);
	const districtIdFromParams = searchParams.get('districtId');

	if (districtIdFromParams) {
		setSessionStorageDistrictId(districtIdFromParams);
		setLocalStorageDistrictId(districtIdFromParams);
	}

	const { data: currentDistrict } = useGetDistrictQuery(
		{ id: sessionStorageDistrictId },
		{ skip: !sessionStorageDistrictId }
	);

	const isLinguistLink =
		(!!currentDistrict?.districtLinguistLinkId && !isAdminRoute) ||
		location.state?.isLinguistLink ||
		searchParams.get('isLinguistLink') === 'true';

	const { data: statusesList } = useGetAllEngagementStatusesQuery();

	const engagementFromLinguistLinkQuery = useGetEngagementFromLinguistLinkQuery(
			!engagementId || !isLinguistLink ? skipToken : { id: engagementId, engagementItemId: engagementItemId }
		);
	const engagementFromQuery = useGetEngagementQuery(
			!engagementId || isLinguistLink ? skipToken : { id: engagementId }
		);
	const {
		data: currentEngagement,
		isError,
		error,
	} = isLinguistLink
		? engagementFromLinguistLinkQuery
		: engagementFromQuery;

	useEffect(() => {
		if (engagementId) {
			setLocalStorageEngagementId(engagementId);
		}

		if (engagementItemId) {
			setLocalStorageEngagementItemId(engagementItemId);
		}

		if (engagementId) {
			dispatch(initiateEngagement({ isLL: isLinguistLink }));
		}
	}, [dispatch, engagementId, isLinguistLink]);

	const { data: existSurvey, error: existSurveyError } = useCheckExistEngagementSurveyQuery(engagementId ?? skipToken);
	const { data: surveyTemplatesList } = useGetAllSurveyTemplatesListQuery(currentEngagement?.districtId ?? skipToken);
	const { data: unrecognizedAnswers } = useGetSurveyUnrecognizedAnswersQuery(
		{ surveyId: existSurvey?._id },
		{ skip: !existSurvey }
	);
	const { sortingArray, handleSortingChange, sortingParams } = useSortingParams();

	const engagementFamiliesWithSurveyLinguistLink = useGetListEngagementFamiliesWithSurveyLinguistLinkQuery(
			!engagementId || !isLinguistLink ? skipToken : { id: engagementId, ...sortingParams }
		);
	const engagementFamiliesWithSurvey = useGetListEngagementFamiliesWithSurveyQuery(
			!engagementId || isLinguistLink ? skipToken : { id: engagementId, ...sortingParams }
		);

	const { data: familiesWithSurvey, isFetching } = isLinguistLink
		? engagementFamiliesWithSurveyLinguistLink
		: engagementFamiliesWithSurvey;

	const { data: surveySum } = useGetSurveySummaryQuery(engagementId ?? skipToken);

	const { data: fieldsListFromDistrict } = useGetAllFieldsQuery(
		{ districtId: currentEngagement?.districtId },
		{ skip: !currentEngagement?.districtId || isLinguistLink }
	);
	const { data: providersList } = useGetAllProvidersByDistrictIdQuery(
		{ districtId: currentEngagement?.districtId },
		{ skip: !currentEngagement?.districtId || isLinguistLink }
	);
	const { data: familiesList } = useGetAllFamiliesByDistrictIdQuery(
		{ district: currentEngagement?.districtId },
		{ skip: !currentEngagement?.districtId }
	);

	const { data: childrenList } = useGetChildrenByDistrictIdQuery(
		{ districtId: currentEngagement?.districtId },
		{ skip: !currentEngagement?.districtId }
	);
	const { data: communicationLogs, isFetching: isFetchingCommunicationLogs } = useGetCommunicationLogQuery(
		{ engagementId },
		{ skip: !engagementId }
	);

	const [generateEngagementsCustomFieldFileUrl] = useGenerateEngagementsCustomFieldFileUrlMutation();

	const [updateEngagement] = useUpdateEngagementMutation();
	const featchActionEdit = async (data: any) => {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		await updateEngagement({
			id: data?._id?.toString(),
			data: {
				...data,
				timezone,
			},
		}).unwrap();
	};
	const handleOpenModalEdit = useOpenModalForm({
		featchAction: featchActionEdit,
		districtId: currentEngagement?.districtId,
		isCreate: false,
		submitButtonName: t('engagements.forms.buttons.save'),
		toastText: 'Engagement record was successfully updated',
		fieldsListFromDistrict,
		providersList,
		familiesList,
		childrenList,
		statusesList,
		generateFileUrlFunction: generateEngagementsCustomFieldFileUrl,
	});

	const [open, setOpen] = useState(false);
	const anchorRef = useRef(null);

	const handleToggle = () => {
		setOpen((prev) => !prev);
	};

	const handleClickAway = () => {
		setOpen(false);
	};

	if (isError) {
		switch ((error as any).data.statusCode) {
			case 404:
			case 400:
				return <NotFoundComponent componentName="engagement" />;
			case 403:
				return <ForbiddenComponent componentName="engagement" />;
			default:
				break;
		}
	}

	return (
		<Container
			maxWidth={false}
			disableGutters
			sx={{
				display: 'flex',
				width: '100%',
				padding: 0,
				margin: 0,
				height: '100vh',
				overflow: 'auto',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					width: '50%',
					height: '100%',
					mr: 1,
				}}
			>
				<Box sx={{ mb: 1 }}>
					<Paper elevation={0} sx={{ width: '100%', height: '100%' }}>
						{currentEngagement?.providerId ? (
							<ProvidersDetails providerId={currentEngagement.providerId} isLinguistLink={isLinguistLink} />
						) : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									width: '100%',
									height: '100%',
									gap: 3,

									padding: 3,
								}}
							>
								<Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px' }}>
									{t('providers.details')}
								</Typography>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										gap: 3,
									}}
								>
									<Box
										style={{
											width: 48,
											height: 48,
											borderRadius: '16px',
											backgroundColor: 'var(--blue-8-opacity, rgba(36, 57, 106, 0.08))',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<AvatarIcon />
									</Box>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<Typography
											sx={{ fontWeight: 500, fontSize: '14px', color: (theme) => theme.colors.grayLightBlue }}
										>
											Provider not chosen yet
										</Typography>
									</Box>
								</Box>
							</Box>
						)}
					</Paper>
				</Box>
				<Paper
					elevation={0}
					sx={{
						width: '100%',
						flexGrow: 1,
						borderBottomRightRadius: 0,
						borderBottomLeftRadius: 0,
						overflowY: 'auto',
					}}
				>
					<EngagementDetails
						isLinguistLink={isLinguistLink}
						engagement={currentEngagement}
						handleEditEngagement={() => {
							handleOpenModalEdit(currentEngagement);
						}}
					/>
				</Paper>
			</Box>
			<Box sx={{ width: '50%', height: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
				<Paper elevation={0} sx={{ width: '100%', maxHeight: '100px', p: '24px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
						<Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px' }}>
								{t('surveys.survey-summary')}
							</Typography>
							<Tooltip title={t('buttons.tooltips.communication-log')}>
								<Button
									ref={anchorRef}
									onClick={handleToggle}
									sx={{
										display: 'flex',
										minWidth: '16px',
										minHeight: '16px',
										width: '32px',
										height: '32px',
										padding: '12px',
										justifyContent: 'center',
										alignItems: 'center',
										borderRadius: '12px',
										backgroundColor: open ? '#1E475E' : '#3C8DBC',
										'&:hover': { backgroundColor: '#307196' },
										'&:active': { backgroundColor: '#1E475E' },
										'&:disabled': { backgroundColor: '#9EC6DE' },
									}}
								>
									<HistoryRoundedIcon sx={{ fontSize: '16px', color: (theme) => theme.colors.white }} />
								</Button>
							</Tooltip>
							<CommunicationLogsModal
								data={communicationLogs}
								open={open}
								handleClickAway={handleClickAway}
								anchorRef={anchorRef}
							/>
						</Box>
						<Typography variant="body1">
							{t('surveys.survey-answered')} {surveySum?.answeredSurveys} of {surveySum?.totalSurveys}
						</Typography>
					</Box>
				</Paper>

				<FamilyDetailsList
					engagementLanguage={currentEngagement?.language ?? ''}
					engagementId={currentEngagement?._id.toString() ?? ''}
					engagementStatus={currentEngagement?.status ?? ''}
					unrecognizedAnswers={unrecognizedAnswers}
					itemsList={familiesWithSurvey}
					isFetching={isFetching}
					engagementSurvey={existSurveyError ? undefined : existSurvey}
					surveyTemplatesList={surveyTemplatesList}
					sortingArray={sortingArray}
					handleSortingChange={handleSortingChange}
					isLinguistLink={isLinguistLink}
				/>
			</Box>
		</Container>
	);
};
